"use client";

import { FC, useEffect, useRef, useState } from "react";
import {
  chartOptions,
  chartPriceFormat,
  graphDurationBtns,
  SchemeReturns,
  sharesColors,
} from "./AMCPerformanceGraphConstants";

import {
  convertChartData,
  getReturnForDuration,
  getUnavailableReturnMessage,
  hasReturnValue,
} from "./AMCPerformanceGraphUtils";

import { createChart } from "lightweight-charts";
import { getAmcChartData } from "../../services/AMCService";
import { IGraph } from "../../interfaces/AMCChartData.interface";
import { getReturnColorClass } from "@repo/shared-utils/num-utils";

import { dataLayer, CombinedClevertapProps } from "@repo/analytics/data-layer-utils";
import { selectLoginStatus, useAppSelector } from "@repo/global-store";

interface AmcPerformanceGraphProps {
  formattedSymbol: string;
  content: IGraph;
  schemeReturns?: SchemeReturns;
}

const CHART_HEIGHT = 225;

const AmcPerformanceGraph: FC<AmcPerformanceGraphProps> = ({
  formattedSymbol,
  content,
  schemeReturns = {},
}) => {
  const chartDivRef = useRef<HTMLDivElement>(null);
  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<{ remove: () => void } | null>(null);
  const latestRequestRef = useRef(0);

  const [duration, setDuration] = useState("1Y");
  const [noData, setNoData] = useState(false);
  const [chartWidth, setChartWidth] = useState(0);

  const loginStatus = useAppSelector(selectLoginStatus);

  const unavailableReturnOptions = {
    unavailableReturnHeading: content?.unavailableReturnHeading,
    durationLabels: content?.durationLabels,
  };

  const parts = content?.graphheading?.split("{returnValue}");
  const hasParts = Boolean(parts?.length);
  const { value: selectedReturn, suffix: returnSuffix } = getReturnForDuration(
    duration,
    schemeReturns
  );
  const showReturnHeading = hasReturnValue(selectedReturn) && hasParts;
  const unavailableReturnMessage = !hasReturnValue(selectedReturn)
    ? getUnavailableReturnMessage(duration, unavailableReturnOptions)
    : null;
  const returnHeadingFallback = unavailableReturnMessage || "";

  const [tooltipData, setTooltipData] = useState<{
    price: number;
    time: string;
    x: number;
  } | null>(null);

  // Dynamic width from parent container
  const updateChartWidth = () => {
    if (chartWrapperRef.current) {
      setChartWidth(chartWrapperRef.current.clientWidth);
    }
  };

  useEffect(() => {
    updateChartWidth();

    window.addEventListener("resize", updateChartWidth);
    return () => window.removeEventListener("resize", updateChartWidth);
  }, []);

  useEffect(() => {
    if (!chartDivRef.current || !chartWidth) return;

    setNoData(false);
    setTooltipData(null);

    if (chartInstanceRef.current) {
      chartInstanceRef.current.remove();
      chartInstanceRef.current = null;
    }

    async function fetchChartData() {
      const requestId = ++latestRequestRef.current;

      try {
        const data = await getAmcChartData({
          duration: duration ?? "1Y",
          bseSchCode: formattedSymbol || "IC1778-DR",
        });

        if (requestId !== latestRequestRef.current) return;

        const chartRawData = data?.data?.info?.navDatalst || [];
        const cdata = convertChartData(chartRawData);

        if (!cdata.length) {
          setNoData(true);
          return;
        }

        if (chartInstanceRef.current) {
          chartInstanceRef.current.remove();
          chartInstanceRef.current = null;
        }

        const chart = createChart(chartDivRef.current!, {
          ...chartOptions,
          width: chartWidth,
          height: CHART_HEIGHT,

          rightPriceScale: {
            scaleMargins: {
              top: 0.1,
              bottom: 0.1,
            },
            entireTextOnly: true,
            borderVisible: false,
          },

          crosshair: {
            mode: 0,
            vertLine: {
              visible: true,
              labelVisible: false,
              width: 1,
              color: "#999",
            },
            horzLine: {
              visible: false,
              labelVisible: false,
            },
          },

          timeScale: {
            ...chartOptions.timeScale,
            visible: false,
            borderVisible: false,
            fixLeftEdge: true,
          },
        });

        chartInstanceRef.current = chart;

        const isRising = (cdata?.[cdata.length - 1]?.value ?? 0) > (cdata?.[0]?.value ?? 0);

        const areaSeries = chart.addAreaSeries({
          priceLineVisible: false,
          lastValueVisible: false,
          priceFormat: chartPriceFormat,
        });

        areaSeries.applyOptions({
          lineColor: isRising ? sharesColors.greenDeep : sharesColors.red,
          lineWidth: 1,
          lastPriceAnimation: 1,
          bottomColor: isRising ? "rgba(52, 209, 120, 0.1)" : "rgba(230, 38, 30, 0.2)",
          topColor: isRising ? sharesColors.greenDeep : sharesColors.red,
        });

        areaSeries?.setData(cdata);

        chart.timeScale().fitContent();
        chart.timeScale().applyOptions({
          rightOffset: 0,
          barSpacing: chartWidth / cdata.length,
        });

        chart.subscribeCrosshairMove((param) => {
          if (param?.time && param?.seriesData?.size > 0 && param?.point) {
            const seriesData = param.seriesData.get(areaSeries);

            const price = seriesData && "value" in seriesData ? seriesData.value : 0;

            let timeStr = "";

            if (typeof param?.time === "number") {
              const date = new Date(param.time * 1000);
              const day = String(date.getUTCDate()).padStart(2, "0");

              const month = date.toLocaleString("en-US", {
                month: "short",
                timeZone: "UTC",
              });

              const year = String(date.getUTCFullYear()).slice(-2);

              const hours = String(date.getUTCHours()).padStart(2, "0");
              const minutes = String(date.getUTCMinutes()).padStart(2, "0");

              timeStr = `${day} ${month} '${year} ${hours}:${minutes}`;
            }

            setTooltipData({
              price,
              time: timeStr,
              x: param.point.x,
            });
          } else {
            setTooltipData(null);
          }
        });
      } catch {
        if (requestId === latestRequestRef.current) {
          setNoData(true);
        }
      }
    }

    fetchChartData();
  }, [duration, formattedSymbol, chartWidth]);

  useEffect(() => {
    return () => {
      chartInstanceRef.current?.remove();
    };
  }, []);

  return (
    <section
      data-cmp="AMCPerformanceGraph"
      data-cmp-type="react"
      data-cmp-title={content?.graphheading || "AMC Performance Graph"}
    >
      <div
        className="flex items-center gap-1.5 py-2 text-xs md:text-base"
        id="amc_performance_graph"
      >
        <span className="text-gray-600">
          {showReturnHeading ? (
            <>
              <span className="block md:inline">{parts[0]}</span>
              <span
                className={`text-base font-medium md:text-lg ${getReturnColorClass(selectedReturn ?? 0)}`}
              >
                {selectedReturn}%
              </span>
              {returnSuffix}
            </>
          ) : (
            returnHeadingFallback
          )}
        </span>
      </div>
      <div className="mt-6 w-full overflow-visible md:mt-3 md:mb-0">
        <div
          ref={chartWrapperRef}
          className="relative w-full"
          style={{ height: `${CHART_HEIGHT}px` }}
        >
          {noData && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
              No Data
            </div>
          )}

          {!noData && tooltipData && (
            <div
              className="text-bfl-blue absolute z-10 text-sm"
              style={{
                top: "-8px",
                left: tooltipData.x < 50 ? "0px" : `${tooltipData.x}px`,
                transform:
                  (tooltipData.x < 50 && "none") ||
                  (tooltipData.x > (chartDivRef.current?.clientWidth ?? 0) - 50 &&
                    "translateX(-100%)") ||
                  "translateX(-50%)",
                pointerEvents: "none",
                whiteSpace: "nowrap",
                fontSize: "12px",
              }}
            >
              ₹{tooltipData?.price.toFixed(2)} | {tooltipData?.time}
            </div>
          )}

          <div className="pt-3 pb-4">
            <div ref={chartDivRef} className="h-[205px] w-full" />
          </div>
        </div>
      </div>

      <div className="mt-5 flex gap-4 text-gray-500 md:mt-[28px]">
        {graphDurationBtns?.map((btnValue) => (
          <button
            key={btnValue}
            type="button"
            onClick={() => {
              setDuration(btnValue);

              const { value: clickReturn, suffix: clickSuffix } = getReturnForDuration(
                btnValue,
                schemeReturns
              );

              const clickUnavailableMessage = hasReturnValue(clickReturn)
                ? null
                : getUnavailableReturnMessage(btnValue, unavailableReturnOptions);

              const sectionTitle = hasReturnValue(clickReturn)
                ? `${parts?.[0] ?? ""}${clickReturn}%${clickSuffix}`
                : clickUnavailableMessage || content?.graphheading || "AMC Performance Graph";

              const payload = {
                component: "amc_performance_graph",
                element: "performance_graph_tab_click",
                event: "click",
                event_type: "tab_click",
                destination_url: "NA",
                section_title: sectionTitle,
                cta_text: btnValue,
                product_name: "bajaj_mutual_fund",
                category_name: "loan",
                page_type: "pdp",
                customer_tag: loginStatus,
                is_loggedin: loginStatus,
                component_position: "",
                component_id: parts?.[0],
              };

              dataLayer(loginStatus, payload, payload as unknown as CombinedClevertapProps);
            }}
            className={`duration-btn cursor-pointer rounded-[25px] px-2 py-1 text-sm ${
              btnValue === duration ? "text-bfl-blue bg-blue-100" : ""
            }`}
          >
            {btnValue}
          </button>
        ))}
      </div>
    </section>
  );
};

export default AmcPerformanceGraph;
